import { ref } from 'vue'
import i18n from '@/i18n'
import { buildInfo } from '@/utils/buildInfo'
import { notifyInfo } from '@/utils/error'
import { logger } from '@/utils/logger'

/**
 * Runtime version detection.
 *
 * Design goals:
 * - Compare /version.json with the current bundle gitSha to detect newer builds.
 * - Run on startup and route changes with a 10-minute throttle.
 * - Show a two-action toast when a new version is available.
 * - Snooze for 30 minutes when the user dismisses or chooses later.
 * - Leave chunk-load failure recovery to router.onError.
 *
 * See buildInfo.ts and the related commit history for more context.
 */

type VersionPayload = {
  version?: string
  gitSha?: string
  buildTime?: string
  env?: string
  webVersion?: string
}

const VERSION_ENDPOINT = '/version.json'
const CHECK_THROTTLE_MS = 10 * 60 * 1000 // 10 minutes
const SNOOZE_DURATION_MS = 30 * 60 * 1000 // 30 minutes
const VERSION_UPDATE_TOAST_ID = 'version-update-available'

let lastCheckedAt = 0
let snoozeUntil = 0
let inflight: Promise<void> | null = null
let notificationVisible = false

const hasNewVersion = ref(false)
const latestVersion = ref<VersionPayload | null>(null)

const fetchLatestVersion = async (): Promise<VersionPayload | null> => {
  try {
    // Add a timestamp to bypass browser, CDN, or proxy caches.
    const response = await fetch(`${VERSION_ENDPOINT}?t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit',
    })
    const raw = await response.text()
    if (!response.ok) {
      logger.warn('[VersionCheck] fetch /version.json failed', {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type'),
      })
      return null
    }
    if (!raw.trim()) return null

    try {
      return JSON.parse(raw) as VersionPayload
    } catch (error) {
      logger.warn('[VersionCheck] invalid /version.json payload', {
        contentType: response.headers.get('content-type'),
        preview: raw.slice(0, 120),
      })
      logger.warn('[VersionCheck] fetch /version.json failed', error)
      return null
    }
  } catch (error) {
    logger.warn('[VersionCheck] fetch /version.json failed', error)
    return null
  }
}

const showUpdateNotification = () => {
  if (notificationVisible) return
  notificationVisible = true

  const t = i18n.global.t
  // Track whether the reload action was used so dismiss handling can decide on snooze.
  let reloading = false

  notifyInfo(t('versionUpdate.title'), {
    id: VERSION_UPDATE_TOAST_ID,
    description: t('versionUpdate.message'),
    duration: Number.POSITIVE_INFINITY, // Let the user choose instead of auto-closing.
    closeButton: true,
    position: 'bottom-right',
    cancel: {
      label: t('versionUpdate.snooze'),
      onClick: () => {
        notificationVisible = false
        snoozeUntil = Date.now() + SNOOZE_DURATION_MS
      },
    },
    action: {
      label: t('versionUpdate.reload'),
      onClick: () => {
        reloading = true
        window.location.reload()
      },
    },
    onDismiss: () => {
      notificationVisible = false
      // Closing or choosing later enters snooze. Reload skips it because the page exits.
      // The later action already sets snoozeUntil; this idempotent update is harmless.
      if (!reloading) {
        snoozeUntil = Date.now() + SNOOZE_DURATION_MS
      }
    },
  })
}

const performCheck = async () => {
  const payload = await fetchLatestVersion()
  if (!payload || !payload.gitSha) return

  latestVersion.value = payload

  // Skip local builds to avoid false positives during hot updates.
  if (buildInfo.gitSha === 'local' || payload.gitSha === 'local') return

  if (payload.gitSha !== buildInfo.gitSha) {
    hasNewVersion.value = true
    showUpdateNotification()
  }
}

/**
 * Trigger one version check.
 * @param force Skip the throttle. Startup should pass true; snooze still applies.
 */
export const checkVersion = async (force = false): Promise<void> => {
  const now = Date.now()
  // Respect the user's later choice even when force is true.
  if (now < snoozeUntil) return
  if (!force && now - lastCheckedAt < CHECK_THROTTLE_MS) return
  if (inflight) return inflight
  lastCheckedAt = now
  inflight = performCheck().finally(() => {
    inflight = null
  })
  return inflight
}

/**
 * Chunk-load fallback: old hashed chunks may disappear after a new deploy.
 * Dynamic import failures cannot be recovered safely, so the app reloads.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false
  const message = String(
    (typeof error === 'object' && error && 'message' in error
      ? (error as { message?: unknown }).message
      : error) ?? '',
  )
  // Cover common Vite, Webpack, and browser error messages.
  return (
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  )
}

export const useVersionCheck = () => ({
  hasNewVersion,
  latestVersion,
  checkVersion,
})
